import { createReducer, on, select } from '@ngrx/store';
import * as FandCActions from './fandc-generator.actions';
import {
  initialFandCGeneratorState,
} from '../types/fandc-generator.state';
import { Frequency, FrequencyOffsetOption } from '../services/frequencyoptions.service';

/** Generates a stable, unique row identifier for a prototype task in the session grid. */
const generatePrototypeRowId = (): string => crypto.randomUUID();

const toDateKey = (value: any): string => {
  if (value == null || value === '') {
    return '';
  }

  const parsed = value instanceof Date ? value : new Date(value as any);
  if (isNaN(parsed.getTime())) {
    return value.toString().trim().toLowerCase();
  }

  return parsed.toISOString().slice(0, 10);
};

const toKey = (value: any): string => (value ?? '').toString().trim().toLowerCase();

const areStringArraysEqual = (left: string[] | null | undefined, right: string[] | null | undefined): boolean => {
  const normalizedLeft = Array.isArray(left) ? left : [];
  const normalizedRight = Array.isArray(right) ? right : [];

  if (normalizedLeft.length !== normalizedRight.length) {
    return false;
  }

  return normalizedLeft.every((value, index) => value === normalizedRight[index]);
};

const getPrototypeRowSignature = (task: any): string => {
  return [
    toKey(task?.taskId),
    toKey(task?.taskName),
    toKey(task?.type),
    toKey(task?.category),
    toKey(task?.frequency),
    toKey(task?.due),
    toKey(task?.format),
    toKey(task?.reportEntity),
    toKey(task?.entityName),
    toDateKey(task?.referenceDate),
    toDateKey(task?.untilDate),
    toDateKey(task?.firstDueDate),
    toKey(task?.reference),
  ].join('|');
};

// Helper function to get the due label from the offset value
const getDueLabel = (frequency: Frequency, offsetValue: string | Date | null, frequencyOffsetOptions: FrequencyOffsetOption[]): string => {
  if (!frequency || !offsetValue || !frequencyOffsetOptions) {
    return offsetValue?.toString() || '';
  }

  // Handle One-Time frequency case where offsetValue is a Date
  if (frequency === 'One-Time' && offsetValue instanceof Date) {
    return offsetValue.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }

  // For other frequencies, find the matching option and return its label
  const offsetValueString = offsetValue?.toString() || '';
  const matchingOption = frequencyOffsetOptions.find(option => option.value === offsetValueString);
  return matchingOption ? matchingOption.label : offsetValueString;
};

export const fandCGeneratorReducer = createReducer(
  initialFandCGeneratorState,
  on(FandCActions.fetchTemplatesSuccess, (state, { data }) => {
    // console.log('fetchTemplatesSuccess:', data);
    // If data exists and has at least one template, set selectedTemplateType
    const selectedTemplateType = data && data.length > 0 ? data[0].templateType : state.selectedTemplateType;
    return {
      ...state,
      selectedTemplateType,
      templates: data,
    };
  }),
  on(FandCActions.fetchTemplateTypesSuccess, (state, { data }) => {
    // console.log('fetchTemplateTypesSuccess:', data);
    return {
      ...state,
      templateTypes: data,
    };
  }),
  on(FandCActions.fetchRootTemplateSuccess, (state, { data }) => {
    // console.log('fetchRootTemplateSuccess:', data);
    return {
      ...state,
      rootTemplate: data,
    };
  }),
  on(FandCActions.fetchDefaultTasksForTemplateSuccess, (state, { data }) => {
      // console.log('fetchDefaultTasksForTemplateSuccess:', data);
      // console.log('state:', state);

      // Guard against null/undefined data to prevent reducer from throwing
      const safeData = data ?? { templateName: null, templateId: null, tasks: [] };

      // Find the label for the selectedReportEntity value
      const reportEntityLabel =
        state.reportEntityOptions && state.selectedReportEntity
        ? (state.reportEntityOptions.find(
          (option: any) => option.value === state.selectedReportEntity
          )?.label ?? state.selectedReportEntity)
        : state.selectedReportEntity ?? '';



    const prototypeTasks = safeData.tasks
      ? safeData.tasks.map((task: any) => ({
          rowId: generatePrototypeRowId(),
          taskId: task.taskId ?? '',
          category: safeData.templateName ?? '',
          taskName: task.taskName ?? '',
          description: task.taskDescription ?? '',
          reportEntity: state.selectedReportEntity ?? '',
          reportEntityLabel: reportEntityLabel,
          entityName: state.selectedEntityName ?? '',
          referenceDate: state.selectedFrequency === 'One-Time' && state.selectedFrequencyOffsetValue && typeof state.selectedFrequencyOffsetValue === 'object' && 'getTime' in (state.selectedFrequencyOffsetValue as any)
            ? state.selectedFrequencyOffsetValue as Date
            : state.selectedReferenceDate,
          firstDueDate: state.firstDueDate,
          untilDate: state.selectedFrequency === 'One-Time' ? null : state.selectedMaturityDate,
          frequency: state.selectedFrequency,
          due: getDueLabel(state.selectedFrequency as Frequency, state.selectedFrequencyOffsetValue, state.frequencyOffsetOptions),
          format: state.selectedFormatOption ?? '',
          reference: task.reference ?? '',
          citation: task.citation ?? '',
          notes: task.notes ?? '',
          assignedUserName: task.assignedUserName ?? '',
          team: task.team ?? '',
          templateId: safeData.templateId ?? '',
          type: task.type ?? '',
        }))
      : [];
    return {
      ...state,
      category: safeData.templateName ?? state.category,
      defaultTasksForTemplate: safeData.tasks,
      prototypeTasks: prototypeTasks,
    };
  }),
  on(FandCActions.updatePrototypeTasks, (state, { changeData }) => {
    // Match rows by rowId (preferred) with taskId as a fallback for legacy hydrated rows.
    const updatedPrototypeTasks = state.selectedPrototypeTasks
      ? state.selectedPrototypeTasks.map(task =>
          (task.rowId && changeData.rowId && task.rowId === changeData.rowId) ||
          (!changeData.rowId && task.taskId === changeData.taskId)
            ? { ...task, ...changeData }
            : task
        )
      : [];
    return {
      ...state,
      selectedPrototypeTasks: updatedPrototypeTasks,
    };
  }),
  on(FandCActions.addToSelectedPrototypeTasks, (state) => {
    // Add new tasks to the existing prototypeTasks array
    if (!state.selectedDefaultTasks || state.selectedDefaultTasks.length === 0) {
      // Return state with selectedPrototypeTasks unchanged (or empty array if undefined)
      return {
        ...state,
        selectedPrototypeTasks: state.selectedPrototypeTasks || [],
        lastGeneratedSelectedDefaultTasks: [],
        lastPrototypeGenerateResult: {
          addedCount: 0,
          duplicateRowIds: [],
          token: (state.lastPrototypeGenerateResult?.token ?? 0) + 1,
        },
      };
    }
    // Filter prototypeTasks to only those whose taskId is in selectedDefaultTasks
    const reportEntityLabel =
      state.reportEntityOptions && state.selectedReportEntity
        ? (state.reportEntityOptions.find(
            (option: any) => option.value === state.selectedReportEntity
          )?.label ?? state.selectedReportEntity)
        : state.selectedReportEntity ?? '';

    const computedReferenceDate =
      state.selectedFrequency === 'One-Time' &&
      state.selectedFrequencyOffsetValue &&
      typeof state.selectedFrequencyOffsetValue === 'object' &&
      'getTime' in (state.selectedFrequencyOffsetValue as any)
        ? (state.selectedFrequencyOffsetValue as any)
        : state.selectedReferenceDate;

    const fallbackReferenceDate =
      computedReferenceDate ??
      state.selectedLoanDetail?.facilityDetail?.originationDate ??
      state.selectedLoanDetail?.facilityDetail?.OriginationDate ??
      null;

    const selectedLoanReference = (
      state.selectedLoanNumber ??
      state.selectedLoanDetail?.facilityDetail?.name ??
      state.selectedLoanDetail?.facilityDetail?.Name ??
      state.selectedLoanDetail?.name ??
      state.selectedLoanDetail?.Name ??
      ''
    )
      .toString()
      .trim();

    const tasksToAdd = (state.prototypeTasks || [])
      .filter(task => state.selectedDefaultTasks.includes(task.taskId))
      .map(task => ({
        rowId: generatePrototypeRowId(),
        ...(() => {
          const rawReferenceDate = task.referenceDate;
          const parsedReferenceDate = rawReferenceDate ? new Date(rawReferenceDate as any) : null;
          const hasValidReferenceDate =
            rawReferenceDate instanceof Date
              ? !isNaN(rawReferenceDate.getTime())
              : !!rawReferenceDate && !!parsedReferenceDate && !isNaN(parsedReferenceDate.getTime());

          return {
            ...task,
            referenceDate: hasValidReferenceDate ? rawReferenceDate : fallbackReferenceDate,
          };
        })(),
        firstDueDate: task.firstDueDate ?? state.firstDueDate,
        reportEntity: state.selectedReportEntity ?? task.reportEntity ?? '',
        reportEntityLabel:
          state.selectedReportEntity
            ? reportEntityLabel
            : (task.reportEntityLabel ?? reportEntityLabel),
        entityName: state.selectedEntityName ?? task.entityName ?? '',
        reference: selectedLoanReference || task.reference || task.referenceName || '',
        citation: task.citation ?? '',
      }));

    const existingRows = (state.selectedPrototypeTasks || []).map((task: any) => ({
      ...task,
      rowId: task?.rowId ?? generatePrototypeRowId(),
    }));
    const hasMaterialSelectionChange = !areStringArraysEqual(
      state.selectedDefaultTasks,
      state.lastGeneratedSelectedDefaultTasks
    );

    const existingSignatures = new Map<string, string>();
    existingRows.forEach((row: any) => {
      existingSignatures.set(getPrototypeRowSignature(row), row.rowId);
    });

    const duplicateRowIds: string[] = [];
    const newRows: any[] = [];

    tasksToAdd.forEach((task: any) => {
      const signature = getPrototypeRowSignature(task);
      if (hasMaterialSelectionChange) {
        newRows.push(task);
        existingSignatures.set(signature, task.rowId);
        return;
      }

      const duplicateRowId = existingSignatures.get(signature);
      if (duplicateRowId) {
        duplicateRowIds.push(duplicateRowId);
        return;
      }

      newRows.push(task);
      existingSignatures.set(signature, task.rowId);
    });

    const updatedSelectedPrototypeTasks = [...existingRows, ...newRows];

    return {
      ...state,
      selectedPrototypeTasks: updatedSelectedPrototypeTasks,
      lastGeneratedSelectedDefaultTasks: [...state.selectedDefaultTasks],
      lastPrototypeGenerateResult: {
        addedCount: newRows.length,
        duplicateRowIds: Array.from(new Set(duplicateRowIds)),
        token: (state.lastPrototypeGenerateResult?.token ?? 0) + 1,
      },
    };
  }),
  on(FandCActions.updateSelectedDefaultTasks, (state, { data }) => {
    return {
      ...state,
      selectedDefaultTasks: data,
    };
  }),
  on(FandCActions.setLoanNumber, (state, { selectedLoan }) => {
    // console.log('setLoanNumber:', selectedLoan?.name);
    const selectedLoanNumber = typeof selectedLoan === 'object'
      ? (selectedLoan as any)?.name ??
        (selectedLoan as any)?.Name ??
        (selectedLoan as any)?.loanNumber ??
        (selectedLoan as any)?.LoanNumber ??
        (selectedLoan as any)?.referenceName ??
        (selectedLoan as any)?.ReferenceName ??
        null
      : (selectedLoan as any) ?? null;

    return { ...state, selectedLoanNumber: selectedLoanNumber };
  }),
  on(FandCActions.setSelectedTemplateType, (state, { templateType }) => {
    // console.log('setSelectedTemplateType:', templateType);
    return { ...state, selectedTemplateType: templateType };
  }),
  on(FandCActions.setCategory, (state, { category }) => {
    // console.log('setCategory:', category);
    return { ...state, category };
  }),
  on(FandCActions.setTasks, (state, { tasks }) => {
    // console.log('setTasks:', tasks);
    return { ...state, tasks };
  }),
  on(FandCActions.setMaturityDate, (state, { selectedMaturityDate }) => {
    // console.log('setMaturityDate:', selectedMaturityDate);
    const updatedPrototypeTasks = state.prototypeTasks
      ? state.prototypeTasks.map(task => ({
        ...task,
        untilDate: selectedMaturityDate
      }))
      : [];
    return { ...state, selectedMaturityDate, prototypeTasks: updatedPrototypeTasks };
  }),
  on(FandCActions.setReferenceDate, (state, { selectedReferenceDate }) => {
    // console.log('setReferenceDate');
    const updatedPrototypeTasks = state.prototypeTasks
      ? state.prototypeTasks.map(task => ({
        ...task,
        referenceDate: selectedReferenceDate
      }))
      : [];
    return { ...state, selectedReferenceDate, prototypeTasks: updatedPrototypeTasks };
  }),
  on(FandCActions.setFrequency, (state, { selectedFrequency }) => {
    // When frequency changes, the due label mapping may also change
    // So we need to update the due label based on the new frequency
    const dueLabel = getDueLabel(selectedFrequency as Frequency, state.selectedFrequencyOffsetValue, state.frequencyOffsetOptions);

    const updatedPrototypeTasks = state.prototypeTasks
        ? state.prototypeTasks.map(task => {
            return {
              ...task,
              frequency: selectedFrequency,
              due: dueLabel,
              untilDate: selectedFrequency === 'One-Time' ? null : state.selectedMaturityDate,
              referenceDate: selectedFrequency === 'One-Time' && state.selectedFrequencyOffsetValue && typeof state.selectedFrequencyOffsetValue === 'object' && 'getTime' in (state.selectedFrequencyOffsetValue as any)
                ? state.selectedFrequencyOffsetValue as Date
                : state.selectedReferenceDate
            };
        })
        : [];
    return { ...state, selectedFrequency, prototypeTasks: updatedPrototypeTasks };
  }),
  on(FandCActions.setFrequencyOffsetValue, (state, { selectedFrequencyOffsetValue }) => {
    const dueLabel = getDueLabel(state.selectedFrequency as Frequency, selectedFrequencyOffsetValue, state.frequencyOffsetOptions);
    const updatedPrototypeTasks = state.prototypeTasks
      ? state.prototypeTasks.map(task => ({
        ...task,
        due: dueLabel,
        untilDate: state.selectedFrequency === 'One-Time' ? null : state.selectedMaturityDate,
        referenceDate: state.selectedFrequency === 'One-Time' && selectedFrequencyOffsetValue && typeof selectedFrequencyOffsetValue === 'object' && 'getTime' in (selectedFrequencyOffsetValue as any)
          ? selectedFrequencyOffsetValue as Date
          : state.selectedReferenceDate
      }))
      : [];
    return { ...state, selectedFrequencyOffsetValue, prototypeTasks: updatedPrototypeTasks };
  }),
  on(FandCActions.setFrequencyOptions, (state, { options }) => {
    return {
      ...state,
      frequencyOptions: options
    };
  }),
  on(FandCActions.setFrequencyOffsetOptions, (state, { options }) => {
    return {
      ...state,
      frequencyOffsetOptions: options
    };
  }),
  on(FandCActions.setFormatOptions, (state, { options }) => {
    return {
      ...state,
      formatOptions: options
    };
  }),
  on(FandCActions.setFormatOption, (state, { selectedFormatOption }) => {
    const updatedPrototypeTasks = state.prototypeTasks
      ? state.prototypeTasks.map(task => ({
        ...task,
        format: selectedFormatOption
      }))
      : [];
    return { ...state, selectedFormatOption, prototypeTasks: updatedPrototypeTasks };
  }),

  on(FandCActions.setFirstDueDate, (state, { firstDueDate }) => ({
    ...state,
    firstDueDate,
    prototypeTasks: state.prototypeTasks
      ? state.prototypeTasks.map(task => ({ ...task, firstDueDate }))
      : []
  })),
  on(FandCActions.fetchLoansSuccess, (state, { data }) => {
    // console.log('fetchLoansSuccess:', data);
    return {
      ...state,
      loans: data,
    };
  }),
  on(FandCActions.fetchLoanDetailSuccess, (state, { data }) => {
    // console.log('fetchLoanDetailSuccess:', data);
    return {
      ...state,
      selectedMaturityDate: data?.facilityDetail?.maturityDate ?? data?.maturityDate ?? state.selectedMaturityDate ?? null,
      selectedReferenceDate: data?.facilityDetail?.originationDate ?? data?.originationDate ?? state.selectedReferenceDate ?? null,
      selectedLoanDetail: data,
    };
  }),
  on(FandCActions.setLoanDetail, (state, { loanDetail }) => {
    // console.log('setLoanDetail:', loanDetail);
    return {
      ...state,
      selectedMaturityDate: loanDetail?.facilityDetail?.maturityDate ?? loanDetail?.maturityDate ?? state.selectedMaturityDate ?? null,
      selectedReferenceDate: loanDetail?.facilityDetail?.originationDate ?? loanDetail?.originationDate ?? state.selectedReferenceDate ?? null,
      selectedLoanDetail: loanDetail,
    };
  }),
  on(FandCActions.fetchReportEntityOptionsSuccess, (state, { data }) => {
    // console.log('fetchReportEntityOptionsSuccess:', data);
    return {
      ...state,
      reportEntityOptions: data,
    };
  }),
  on(FandCActions.setReportEntity, (state, { selectedReportEntity }) => {
    const reportEntityLabel =
      state.reportEntityOptions && selectedReportEntity
        ? (state.reportEntityOptions.find(
            (option: any) => option.value === selectedReportEntity
          )?.label ?? selectedReportEntity)
        : selectedReportEntity ?? '';

    const updatedPrototypeTasks = state.prototypeTasks
      ? state.prototypeTasks.map(task => ({
          ...task,
          reportEntity: selectedReportEntity ?? '',
          reportEntityLabel: selectedReportEntity
            ? reportEntityLabel
            : task.reportEntityLabel ?? reportEntityLabel,
        }))
      : [];

    return {
      ...state,
      prototypeTasks: updatedPrototypeTasks,
      selectedReportEntity: selectedReportEntity,
    };
  }),
  on(FandCActions.setEntityNameOptions, (state, { options }) => {
    // console.log('setEntityNameOptions:', options);
    return {
      ...state,
      entityNameOptions: options,
    };
  }),
  on(FandCActions.setEntityName, (state, { selectedEntityName }) => {
      // Find the label for the selectedReportEntity value
      const reportEntityLabel =
        state.reportEntityOptions && state.selectedReportEntity
        ? (state.reportEntityOptions.find(
          (option: any) => option.value === state.selectedReportEntity
          )?.label ?? state.selectedReportEntity)
        : state.selectedReportEntity ?? '';
      // console.log('setEntityName:', selectedEntityName);
      // console.log('reportEntityLabel:', reportEntityLabel);
      // Update prototype tasks with the latest entity name and report entity info
      const updatedPrototypeTasks = state.prototypeTasks
        ? state.prototypeTasks.map(task => ({
            ...task,
            entityName: selectedEntityName,
            reportEntity: state.selectedReportEntity ?? task.reportEntity ?? '',
            reportEntityLabel: state.selectedReportEntity
              ? reportEntityLabel
              : task.reportEntityLabel ?? reportEntityLabel
          }))
        : [];

        return {
      ...state,
      prototypeTasks: updatedPrototypeTasks,
      selectedEntityName: selectedEntityName,
    };
  }),
  on(FandCActions.fetchGeneratedTasksSuccess, (state, { tasks }) => {
    // console.log('fetchGeneratedTasksSuccess:', tasks);
    return {
      ...state,
      generatedTasks: tasks,
    };
  }),
  on(FandCActions.updateGeneratedTasks, (state, { changeData }) => {
    // changeEvent should have: key (taskId), changes (partial task)
    // console.log('updateGeneratedTasks:', changeData);
    const updatedGeneratedTasks = state.prototypeTasks
      ? state.generatedTasks.map(task =>
          task.taskId === changeData.taskId
            ? { ...task, ...changeData }
            : task
        )
      : [];
      // console.log('updatedGeneratedTasks:', updatedGeneratedTasks);
    return {
      ...state,
      generatedTasks: updatedGeneratedTasks,
    };
  }),
  on(FandCActions.setSelectedPrototypeTasks, (state, { selectedPrototypeTasks }) => {
    return {
      ...state,
      selectedPrototypeTasks: (selectedPrototypeTasks || []).map((task: any) => ({
        ...task,
        rowId: task?.rowId ?? generatePrototypeRowId(),
        citation: task?.citation ?? '',
      })),
    };
  }),
  on(FandCActions.removePrototypeTask, (state, { rowId }) => {
    // AC-4: filter exclusively on rowId when present. The taskId fallback handles legacy
    // state rows that were hydrated before the uuid-keyed rowId contract was introduced.
    const filteredTasks = state.selectedPrototypeTasks.filter(
      task => task.rowId !== undefined ? task.rowId !== rowId : task.taskId !== rowId
    );
    return {
      ...state,
      selectedPrototypeTasks: filteredTasks,
    };
  }),
  on(FandCActions.setTaskId, (state, { taskId }) => {
    return {
      ...state,
      taskId: taskId,
    };
  }),
  on(FandCActions.setTaskGenerationMetadataId, (state, { taskGenerationMetadataId }) => {
    return {
      ...state,
      taskGenerationMetadataId: taskGenerationMetadataId,
    };
  }),
  on(FandCActions.setExistingTaskIds, (state, { existingTaskIds }) => {
    return {
      ...state,
      existingTaskIds: existingTaskIds ?? [],
    };
  }),
  on(FandCActions.clearEditModeFormSelections, (state) => {
    return {
      ...state,
      selectedTemplateType: null,
      category: null,
      selectedDefaultTasks: [],
      selectedFrequency: null,
      selectedFrequencyOffsetValue: null,
      selectedFormatOption: null,
      selectedReportEntity: null,
      selectedEntityName: null,
      selectedReferenceDate: null,
      firstDueDate: null,
      selectedReviewerId: null,
      selectedReviewerName: null,
    };
  }),
  on(FandCActions.fetchReviewersSuccess, (state, { reviewers }) => ({
    ...state,
    reviewerList: reviewers,
  })),
  on(FandCActions.setReviewer, (state, { reviewerId, reviewerName }) => ({
    ...state,
    selectedReviewerId: reviewerId,
    selectedReviewerName: reviewerName,
  })),
  on(FandCActions.resetFandCGenerator, () => {
    // console.log('resetFandCGenerator');
    return initialFandCGeneratorState;
  })
);
